import { useEffect, useState } from "react";
import { MessageSquareText, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export default function DispatchSmsCard({ canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [template, setTemplate] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/dispatch-sms").then((r) => { setCfg(r.data); setTemplate(r.data.template); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;

  const dirty = template !== cfg.template;
  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/dispatch-sms", { enabled: cfg.enabled, template, ...patch });
      setCfg(data); setTemplate(data.template);
      toast.success(patch.enabled === false ? "Dispatch SMS off" : "Dispatch SMS saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testPhone.trim()) return toast.error("Enter a mobile number");
    if (dirty) await save();
    setBusy(true);
    try { await api.post("/settings/dispatch-sms/test", { phone: testPhone }); toast.success(`Test SMS sent to ${testPhone}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };
  const insert = (ph) => setTemplate((t) => `${t}${t.endsWith(" ") || !t ? "" : " "}${ph}`);

  return (
    <div className="card-industrial p-6 space-y-5" data-testid="dispatch-sms-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <MessageSquareText className="w-5 h-5 text-primary-orange" />
          <div>
            <div className="mono uppercase text-xs tracking-widest text-muted-industrial">Customer notifications</div>
            <h3 className="font-heading font-black text-xl uppercase">Dispatch SMS</h3>
          </div>
        </div>
        <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${cfg.configured ? "border-emerald-500 text-emerald-400" : "border-amber-500 text-amber-400"}`} data-testid="dispatch-sms-status">
          {cfg.configured ? `Live · from ${cfg.from_number}` : "Twilio not configured"}
        </span>
      </div>
      <p className="text-xs text-muted-industrial">
        Sent to the site contact the moment a driver taps <b>Depart Plant</b>. Contact is taken from the saved site, then the customer's contacts, then the customer's main phone.
      </p>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={cfg.enabled} disabled={!canEdit || busy} onCheckedChange={(v) => save({ enabled: !!v })} className="mt-0.5 h-5 w-5 rounded-none border-industrial" data-testid="dispatch-sms-toggle" />
        <div>
          <div className="mono uppercase text-xs tracking-widest font-bold">Text the customer on every dispatch</div>
          <div className="text-[10px] uppercase tracking-wider opacity-70">Untick to pause all customer dispatch texts plant-wide</div>
        </div>
      </label>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="mono uppercase text-[10px] tracking-widest text-muted-industrial">Message template · {template.length}/480</div>
          {canEdit && template !== cfg.default && (
            <button type="button" onClick={() => setTemplate(cfg.default)} className="mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline inline-flex items-center gap-1" data-testid="dispatch-sms-reset">
              <RotateCcw className="w-3 h-3" /> Reset to default
            </button>
          )}
        </div>
        <textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          disabled={!canEdit}
          rows={3}
          maxLength={480}
          className="w-full bg-transparent border border-industrial p-3 mono text-sm rounded-none focus:outline-none focus:border-primary-orange"
          data-testid="dispatch-sms-template"
        />
        <div className="flex flex-wrap gap-1.5">
          {cfg.placeholders.map(([ph, help]) => (
            <button key={ph} type="button" title={help} disabled={!canEdit} onClick={() => insert(ph)}
              className="mono text-[10px] px-2 py-0.5 border border-industrial hover:border-primary-orange hover:text-primary-orange transition-colors" data-testid={`sms-ph-${ph.replace(/[{}]/g, "")}`}>
              {ph}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-industrial bg-black/20 p-3" data-testid="dispatch-sms-preview">
        <div className="mono uppercase text-[10px] tracking-widest text-muted-industrial mb-1">Preview</div>
        <div className="text-sm">{cfg.preview}</div>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => save()} disabled={busy || !dirty} className="rounded-none bg-primary-orange text-black hover:bg-primary-orange/90 uppercase text-[10px] tracking-widest h-9" data-testid="dispatch-sms-save">
            Save template
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="04xx xxx xxx" className="rounded-none bg-transparent border-industrial mono h-9 w-40" data-testid="dispatch-sms-test-phone" />
            <Button variant="outline" onClick={sendTest} disabled={busy || !cfg.configured} className="rounded-none border-industrial bg-transparent uppercase text-[10px] tracking-widest h-9" data-testid="dispatch-sms-test-send">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send test
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
